import {useState, useEffect, useContext, useMemo} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import MaterialTable, {Action} from "@material-table/core";
import TableStyle from "@components/TableStyle";
import {QuotationAttachmentSchema, QuotationAttachmentsTableProps} from '../types'
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import { NotificationsContext } from '@ui-components/Notifications';
import {getSignedUrl, downloadFile, uploadFileToS3} from '@pages/WorksitesDocuments/utils/FilesAndS3Utils';
import CountryContext from "../../../CountryContext";
import { tableIcons } from '@ui-components/TableIcons';
import {download, getColumns} from "@feature/quotations/quotation-attachments-table/lib/utilities";
import {ACCEPTED_FORMATS, MAX_SIZE, OPTIONS} from './lib/consts';
import Button from "@ui-components/Button";
import {ArrowUpTrayIcon} from "@heroicons/react/24/outline";
import authenticationService from "@services/AuthenticationService";


export const QuotationAttachmentsTable = ({
  quotation,
}: QuotationAttachmentsTableProps) => {

  // context
  const { push } = useContext(NotificationsContext);
  const {
    country: { code },
  } = useContext(CountryContext);

  // state
  const [attachments, setAttachments]
    = useState<QuotationAttachmentSchema[]>([]);
  const [loading, setLoading] = useState(false);

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  const userData = authenticationService.getUserData();

  const fetchAttachments = async () => {
    const params = {id_quotation: quotation.id};
    const response: QuotationAttachmentSchema[] = await countryApiRequest!
      .get("/quotations_attachments/all", {params});
    setAttachments(response);
  }

  useEffect(() => {
    fetchAttachments().then();
  }, [quotation])

  // utilities

  // memos
  const columns = useMemo(
    () => getColumns(intl, countryApiRequest),
    [intl, countryApiRequest])
  ;

  return (
    <TableStyle>
      <MaterialTable<QuotationAttachmentSchema>
        title={
          intl.formatMessage({ id: 'attachments', defaultMessage: 'Allegati' })
        }
        options={OPTIONS}
        columns={columns}
        data={attachments}
        actions={[
          ({s3_url, name}) => ({
            icon: tableIcons.Download,
            tooltip: intl.formatMessage({ id: 'download', defaultMessage: 'Scarica' }),
            onClick: async () => download(
              countryApiRequest, s3_url, name, 'quotations_attachments', downloadFile
            ),
          }) as Action<QuotationAttachmentSchema>,
          (rowData) => rowData.w_perm.includes(`user_${userData.id}`) ? ({
            icon: tableIcons.Delete,
            tooltip: intl.formatMessage({ id: 'delete', defaultMessage: 'Elimina' }),
            onClick: async () => {
              // TODO move to dedicated hook
              setLoading(true)
              try {
                await countryApiRequest!.delete(`/quotations_attachments/${rowData.id}`);
                await fetchAttachments()
              } catch (e) {
                push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
              }
              setLoading(false)
            }
          }) as Action<QuotationAttachmentSchema>: null,
          {
            icon: () => <ArrowUpTrayIcon className="w-5 mr-1" />,
            tooltip: intl.formatMessage({ id: 'add', defaultMessage: 'Aggiungi' }),
            isFreeAction: true,
            onClick: () => {
              // TODO move to dedicated hook
          		const fileLoader = document.createElement('input')
          		fileLoader.type = 'file'
          		fileLoader.multiple = true
          		fileLoader.className = 'hidden'
          		fileLoader.accept = ACCEPTED_FORMATS.join(", ")
              // @ts-ignore
          		fileLoader.onchange = async (e:any) => {
          			setLoading(true)
          			let newList:(any[]) = [];
          			let size = 0;
          			Object.keys(e.target.files).forEach((key) => {
          				newList.push(e.target.files[key]);
          				size += e.target.files[key].size;
          			});
          			if (size > MAX_SIZE) {
          				push({type: 'error', title: intl.formatMessage({id: 'size_limit_exceeded'})});
          			} else {
          				const filesUploaded = await Promise.all(newList.map(async (file) => {
          					try {
          						const url = await uploadFileToS3(
          							countryApiRequest,
          							file,
          							`quotations_attachments/${code}/${quotation.id}`,
          							'documents',
          						);
          						return {
          							name: file.name,
          							size: file.size,
          							s3_url: url,
          						}
          					} catch (e) {
          						push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
          					}
          				}));
          				// add files calling post to backend
          				for (let file of filesUploaded.filter(f => f)) {
                    // @ todo: add post request to insert attachment in db
                    await countryApiRequest!.post(`/quotations/attachment`, {
                      id_quotation: quotation.id,
                      name: file!.name,
                      size: file!.size,
                      s3_url: file!.s3_url,
                    });
          				}
          			}
                // @ todo add refetch of attachments
                await fetchAttachments()
          			setLoading(false)
          		}
          		fileLoader.click()
            }
          }
        ]}
        localization={{
          header: {actions: ''}
        }}
      />
    </TableStyle>
  )
}